import { type ReactElement } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Text,
  Skeleton,
  LinearProgress,
} from '@citibox-panel/ui';
import { useDoorAccessCounts } from '../hooks/useDoorAccessCounts';
import { computeCoverage, formatCount, formatPercentage, type Coverage } from '../lib/coverage';
import { ReadErrorStrip } from '../../../components/ReadErrorStrip';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface CoverageCardProps {
  fetchCount: (enabled: boolean) => Promise<number>;
  isHandledByHost?: IsHandledByHost;
}

const BAR_LABEL = 'Ubicaciones activadas sobre configuradas';
// Fixed panel copy. The server's own `message`/`detail` never reaches the DOM — an error envelope is
// attacker-influenced data like any other payload — and the card switches on no `code` string
// either, because the codes on this route are lowercase while other endpoints send them uppercase.
const READ_ERROR = 'No se ha podido cargar el recuento de ubicaciones.';
const DOT_SIZE = 8;

function LegendDot({ color }: { color: string }): ReactElement {
  return (
    <Box sx={{ width: DOT_SIZE, height: DOT_SIZE, borderRadius: '50%', backgroundColor: color }} />
  );
}

function LoadingBody(): ReactElement {
  return (
    <>
      <Skeleton variant="text" width={160} height={56} />
      <Skeleton variant="rounded" height={DOT_SIZE} />
    </>
  );
}

// `component="p"` matters: MUI maps variant→element, so a bare `variant="h3"` would put the number
// itself into the document's heading outline, beside the card's own label.
function HeadlineCount({ count }: { count: number }): ReactElement {
  return (
    <Text variant="h3" component="p">
      {formatCount(count)}
    </Text>
  );
}

function CoverageBody({
  coverage,
  disabledCount,
}: {
  coverage: Coverage;
  disabledCount: number;
}): ReactElement {
  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 1 }}>
        <Text variant="body1">{`de ${formatCount(coverage.total)} configuradas`}</Text>
        <Text variant="body1" color="brand" bold>
          {formatPercentage(coverage.percentage)}
        </Text>
      </Stack>
      <LinearProgress value={coverage.percentage} aria-label={BAR_LABEL} />
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mt: 1.5 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <LegendDot color="primary.main" />
          <Text variant="body2">Activadas</Text>
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <LegendDot color="divider" />
          {/* «desactivadas», never «sin acceso»: every unconfigured location also has no access,
              and those are not in this denominator at all. */}
          <Text variant="body2">{`${formatCount(disabledCount)} desactivadas`}</Text>
        </Stack>
      </Stack>
    </>
  );
}

// The five states the card can be in, each carrying exactly the data its body needs. The two
// reads are deliberately not symmetric, and a discriminated union is what keeps that asymmetry
// legible: `error` is the ENABLED read failing (there is no number to show without it), while a
// failed disabled read leaves `partial` — a number with no denominator, share or bar.
type CardState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'empty' }
  | { kind: 'partial'; enabledCount: number }
  | { kind: 'populated'; enabledCount: number; disabledCount: number; coverage: Coverage };

function resolveState({
  enabledCount,
  disabledCount,
  isPending,
  isError,
}: {
  enabledCount: number | undefined;
  disabledCount: number | undefined;
  isPending: boolean;
  isError: boolean;
}): CardState {
  if (isError) {
    return { kind: 'error' };
  }
  if (isPending || enabledCount === undefined) {
    return { kind: 'loading' };
  }
  if (disabledCount === undefined) {
    return { kind: 'partial', enabledCount };
  }
  const coverage = computeCoverage(enabledCount, disabledCount);
  if (coverage === null) {
    return { kind: 'empty' };
  }
  return { kind: 'populated', enabledCount, disabledCount, coverage };
}

function CardBody({ state, onRetry }: { state: CardState; onRetry: () => void }): ReactElement {
  switch (state.kind) {
    case 'loading':
      return <LoadingBody />;
    case 'error':
      return <ReadErrorStrip message={READ_ERROR} onRetry={onRetry} />;
    case 'empty':
      // The table is deny-by-default, so an untouched network has no rows and therefore no
      // denominator. Stating that beats dividing anyway and printing `NaN %`.
      return <Text variant="body1">Sin ubicaciones configuradas</Text>;
    case 'partial':
      return <HeadlineCount count={state.enabledCount} />;
    case 'populated':
      return (
        <>
          <HeadlineCount count={state.enabledCount} />
          <CoverageBody coverage={state.coverage} disabledCount={state.disabledCount} />
        </>
      );
  }
}

// TODO: i18n — copy en ES hasta Fase 1.
export function CoverageCard({
  fetchCount,
  isHandledByHost = neverHandledByHost,
}: CoverageCardProps): ReactElement {
  const { enabledCount, disabledCount, isPending, isError, error, retry } =
    useDoorAccessCounts(fetchCount);
  // An error the host already shows is not the card's to report: with no number to show, the card
  // simply stays on its skeleton behind the host's screen.
  const showsError = isError && !isHandledByHost(error);

  // Elevated, not outlined: the card sits ON the page's white panel, where a hairline border would
  // vanish into the surface behind it. The shadow is what separates the counter from that panel, and
  // level 4 rather than the resting 1 is what makes that separation legible — white on white needs a
  // wider, more diffuse ambient layer (10px against 3px) than a card on the page gray does. See
  // docs/DESIGN.md §7.
  return (
    <Card variant="elevation" elevation={4} sx={{ borderRadius: 3 }}>
      <CardContent>
        {/* The card's only accessible name. `component="h2"` is what puts it in the document
            outline: with several cards on the page, heading navigation is how an AT user moves
            between them. */}
        <Text variant="overline" component="h2" bold dense>
          UBICACIONES ACTIVADAS
        </Text>
        <CardBody
          state={resolveState({ enabledCount, disabledCount, isPending, isError: showsError })}
          onRetry={retry}
        />
        {/* Half a step off the body above it: the caption QUALIFIES the number, so it must not read
            as another line of it — but it belongs to it, so a full step would detach the two. The
            margin rides a Box because the façade Text takes no spacing of its own. */}
        <Box sx={{ mt: 0.5 }}>
          {/* ⚠️ `secondary` (#ACA5BB) measures 2.37:1 on white — under AA's 4.5:1 for 12px text, and
              the ONE deliberate exception to this slice's «informational text keeps the body tone»
              invariant. Signed off on 2026-08-26: the caption qualifies the number rather than
              carrying it, and the qualification is redundant with «de N configuradas» above. The
              exception is this line and nothing else — do not copy it to the legend or the readout,
              which the reader has to act on. */}
          <Text variant="caption" component="p" color="secondary">
            No incluye las ubicaciones sin configurar, que no tienen acceso por defecto
          </Text>
        </Box>
      </CardContent>
    </Card>
  );
}
